import type { CurrentUser } from "@/features/user/types";
import {
  JWSRegisteredHeaders,
  JWTRegisteredClaims,
  joseAlgorithmHS256,
  parseJWT,
} from "@oslojs/jwt";
import { createServerFn } from "@tanstack/react-start";
import { getCookie } from "@tanstack/react-start/server";
import { tryit } from "radashi";
import { authService } from "./backend";
import { type Session, validateSessionToken } from "./session";

export type SessionValidationResult =
  | { session: Session; user: CurrentUser }
  | { session: null; user: null };

export const getSessionToken = createServerFn({
  method: "GET",
}).handler<string | null>(async () => {
  const sessionToken = getCookie("session");
  if (!sessionToken) {
    return null;
  }
  const session = await validateSessionToken(sessionToken);
  if (!session) {
    return null;
  }
  return sessionToken;
});

export const getCurrentSession = createServerFn({
  method: "GET",
}).handler<SessionValidationResult>(async () => {
  const sessionToken = getCookie("session");
  if (!sessionToken) {
    return { session: null, user: null };
  }
  const session = await validateSessionToken(sessionToken, verifySessionToken);
  if (!session) {
    return { session: null, user: null };
  }

  const [err, user] = await tryit(authService.getUserByToken)({
    data: sessionToken,
  });
  if (err) {
    return { session: null, user: null };
  }
  return { session, user };
});

export function verifySessionToken(token: string): { userId: string } {
  const [header, payload, _signature] = parseJWT(token);
  const headerParameters = new JWSRegisteredHeaders(header);
  if (headerParameters.algorithm() !== joseAlgorithmHS256) {
    throw new Error("Unsupported algorithm");
  }
  const claims = new JWTRegisteredClaims(payload);
  // TODO: Backend not supporting expiration yet
  // if (!claims.verifyExpiration()) {
  //   throw new Error("Expired token");
  // }
  if (!claims.hasSubject()) {
    throw new Error("Missing subject");
  }
  return {
    userId: claims.subject(),
  };
}
